import { useRef, useState } from "react";
import { HelpIcon, type HelpIconProps, type HelpIconSize } from "./HelpIcon";
import { HelpTopicChooser } from "./HelpTopicChooser";
import { useGuidedTour } from "./useGuidedTour";
import type { HelpStep, HelpTopic } from "./types";

export type { HelpStep, HelpTopic } from "./types";

export interface HelpProps
	extends Omit<HelpIconProps, "onClick" | "disabled"> {
	/**
	 * Joyride/Help steps for a single flat tour. Mutually exclusive with
	 * `topics` — the icon is disabled when neither has any entries.
	 */
	guideSteps?: HelpStep[];
	/**
	 * When provided, the icon opens a topic chooser ("What would you like to
	 * know more about?") instead of starting a tour directly; picking a topic
	 * starts its `steps`. Mutually exclusive with `guideSteps`.
	 */
	topics?: HelpTopic[];
	/** Fired once the tour completes or is dismissed. */
	onAfterShowMe?: () => void;
	/** Fired before the tour starts. Return `false` to cancel. */
	onBeforeShowMe?: () => boolean | void | Promise<boolean | void>;
}

/**
 * Help — drop-in trigger + guided tour.
 *
 * Composes `useGuidedTour` (lifecycle, step orchestration) with `HelpIcon`
 * (presentation). For custom triggers — menu items, banner buttons, etc. —
 * use `useGuidedTour` directly and render your own UI.
 *
 * PRO-730: pass `topics` instead of `guideSteps` to offer a "What would you
 * like to know more about?" chooser first — the icon opens `HelpTopicChooser`,
 * and selecting a topic closes it, then starts that topic's tour. The chooser
 * is fully closed before `start()` is called so Joyride never measures a
 * target while still covered by the chooser's overlay.
 */
export const Help: React.FC<HelpProps> = ({
	guideSteps,
	topics,
	onBeforeShowMe,
	onAfterShowMe,
	...iconProps
}) => {
	const hasTopics = Boolean(topics && topics.length > 0);
	const [isChooserOpen, setIsChooserOpen] = useState(false);
	const [activeSteps, setActiveSteps] = useState<HelpStep[]>([]);

	const { start, TourPortal } = useGuidedTour({
		steps: hasTopics ? activeSteps : guideSteps ?? [],
		onBeforeStart: onBeforeShowMe,
		onAfterEnd: onAfterShowMe,
	});

	// `useGuidedTour`'s `start` only actually starts the tour once it closes
	// over a non-empty `steps` array (it no-ops otherwise). Calling it
	// synchronously from `handleSelectTopic` — in the same tick as
	// `setActiveSteps` — would still be the PREVIOUS render's `start`, bound
	// while `activeSteps` was still `[]` (no topic picked yet), which bails
	// immediately. `startRef` always holds the LATEST `start`, updated every
	// render (a plain assignment, not effect-gated) — reading it from a
	// `requestAnimationFrame` callback scheduled by the click handler picks up
	// the version from the render that already applied the topic's steps,
	// once React has re-rendered. That same frame's wait also gives the
	// chooser's Modal a tick to finish unmounting before Joyride starts
	// measuring targets.
	const startRef = useRef(start);
	startRef.current = start;

	const handleTriggerClick = () => {
		if (hasTopics) {
			setIsChooserOpen(true);
			return;
		}
		void start();
	};

	const handleCloseChooser = () => {
		setIsChooserOpen(false);
	};

	const handleSelectTopic = (topic: HelpTopic) => {
		setIsChooserOpen(false);
		setActiveSteps(topic.steps);
		requestAnimationFrame(() => {
			void startRef.current();
		});
	};

	const hasSteps = hasTopics
		? (topics?.length ?? 0) > 0
		: (guideSteps?.length ?? 0) > 0;

	return (
		<>
			<HelpIcon {...iconProps} onClick={handleTriggerClick} disabled={!hasSteps} />
			{hasTopics && (
				<HelpTopicChooser
					isOpen={isChooserOpen}
					topics={topics ?? []}
					onClose={handleCloseChooser}
					onSelect={handleSelectTopic}
				/>
			)}
			{TourPortal}
		</>
	);
};

export type { HelpIconProps, HelpIconSize };
